import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Shield, Zap, Lock, ChevronRight, Eye, Terminal, Globe } from "lucide-react";
import axios from "axios";
import Admin from "./Admin";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_exclusive-access-11/artifacts/bkbdmyhu_logo_light.webp";
const CHIP_URL = "https://customer-assets.emergentagent.com/job_exclusive-access-11/artifacts/ffljkbwt_image.png";
const WEEZY_URL = "https://customer-assets.emergentagent.com/job_exclusive-access-11/artifacts/chq0b3y8_PHOTO-2025-11-26-18-42-11.jpg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Supported languages
const LANGUAGES = {
  "en": "EN",
  "fr": "FR",
  "fi": "FI",
  "de": "DE",
  "ja": "日本語",
  "pt-br": "PT-BR",
  "es": "ES"
};

// Country to language mapping
const COUNTRY_LANGUAGE_MAP = {
  "FR": "fr", "BE": "fr", "CH": "fr", "CA": "fr", "LU": "fr",
  "FI": "fi",
  "DE": "de", "AT": "de",
  "JP": "ja",
  "BR": "pt-br",
  "ES": "es", "MX": "es", "AR": "es", "CO": "es", "PE": "es",
  "VE": "es", "CL": "es", "EC": "es", "GT": "es", "CU": "es",
  "BO": "es", "DO": "es", "HN": "es", "PY": "es", "SV": "es",
  "NI": "es", "CR": "es", "PA": "es", "UY": "es", "PR": "es"
};

// Default feature icons
const featureIcons = [Shield, Zap, Lock];

// Falling Casino Chips Component
const FallingChips = ({ intense = false }) => {
  const chipCount = intense ? 25 : 15;
  const baseOpacity = intense ? 0.35 : 0.25;
  
  const chips = Array.from({ length: chipCount }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 8,
    duration: 8 + Math.random() * 12,
    size: 40 + Math.random() * 60,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 720,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
      {chips.map((chip) => (
        <motion.div
          key={chip.id}
          className="absolute"
          style={{
            left: `${chip.left}%`,
            top: -100,
            width: chip.size,
            height: chip.size,
          }}
          initial={{ y: -100, rotate: chip.rotation, opacity: 0 }}
          animate={{
            y: ["0vh", "110vh"],
            rotate: [chip.rotation, chip.rotation + chip.rotationSpeed],
            opacity: [0, baseOpacity, baseOpacity, 0],
          }}
          transition={{
            duration: chip.duration,
            delay: chip.delay,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          <img
            src={CHIP_URL}
            alt=""
            className="w-full h-full object-contain"
            style={{ filter: intense ? "brightness(0.8)" : "brightness(0.7) saturate(0.9)" }}
          />
        </motion.div>
      ))}
    </div>
  );
};

// Detect user's country and return appropriate language
const detectLanguage = async () => {
  // Check localStorage first
  const savedLang = localStorage.getItem("unmarketed_lang");
  if (savedLang && LANGUAGES[savedLang]) {
    return savedLang;
  }
  
  try {
    // Use free IP geolocation API
    const response = await axios.get("https://ipapi.co/json/", { timeout: 3000 });
    const countryCode = response.data.country_code;
    const detectedLang = COUNTRY_LANGUAGE_MAP[countryCode] || "en";
    localStorage.setItem("unmarketed_lang", detectedLang);
    return detectedLang;
  } catch {
    return "en";
  }
};

// Hero Section
const HeroSection = ({ settings }) => {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const scale = useTransform(scrollY, [0, 400], [1, 0.95]);
  const y = useTransform(scrollY, [0, 400], [0, 100]);

  const statusBar = settings?.status_bar || "ACCESS POINT DETECTED";
  const tagline = settings?.tagline || "NOT FOR EVERYONE.";
  const taglineHighlight = settings?.tagline_highlight || "SELECTED FEW ONLY.";
  const heroCta = settings?.hero_cta || "FIND THE GATE";

  return (
    <motion.section 
      style={{ opacity, scale }}
      className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Glow */}
      <div className="glow-red top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2" />
      <div className="glow-red bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 opacity-50" />
      
      {/* Grid Background */}
      <div className="absolute inset-0 grid-bg opacity-30" />
      
      {/* Content */}
      <motion.div 
        style={{ y }}
        className="relative z-10 flex flex-col items-center text-center max-w-5xl"
      >
        {/* Status Bar */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-8 px-4 py-2 glass rounded-full"
          data-testid="status-bar"
        >
          <div className="status-dot" />
          <span className="terminal-text text-xs tracking-widest uppercase">
            <span className="prompt">[</span>SYSTEM<span className="prompt">]</span> {statusBar}
          </span>
        </motion.div>

        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="logo-container mb-8"
          data-testid="logo-container"
        >
          <img 
            src={LOGO_URL} 
            alt="UNMARKETED" 
            className="w-full max-w-md md:max-w-xl h-auto"
            data-testid="logo-image"
          />
        </motion.div>

        {/* Tagline */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="terminal-text text-sm md:text-base mb-12 tracking-wider"
          data-testid="tagline"
        >
          <span className="prompt">{'>'}</span> {tagline} <span className="success">{taglineHighlight}</span>
        </motion.p>

        {/* CTA Button */}
        <motion.button
          onClick={() => document.getElementById('access-section').scrollIntoView({ behavior: 'smooth' })}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="cyber-btn group"
          data-testid="main-cta"
        >
          <span className="relative z-10 flex items-center gap-3">
            {heroCta}
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </motion.button>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          data-testid="scroll-indicator"
        >
          <motion.div 
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-px h-16 bg-gradient-to-b from-neon-red to-transparent"
          />
        </motion.div>
      </motion.div>
    </motion.section>
  );
};

// Features Section
const FeaturesSection = ({ settings }) => {
  const defaultFeatures = [
    { title: "VERIFIED SECURE", description: "End-to-end encryption. Your identity stays hidden.", status: "ACTIVE" },
    { title: "INSTANT ACCESS", description: "No waiting lists. No verification delays.", status: "LIVE" },
    { title: "EXCLUSIVE ENTRY", description: "Selected members only. You found the gate.", status: "UNLOCKED" }
  ];
  
  const displayFeatures = settings?.features || defaultFeatures;
  const sectionTitle = settings?.section_title || "WHY UNMARKETED";
  const sectionSubtitle = settings?.section_subtitle || "$ cat /protocols/trust.log";

  return (
    <section 
      className="relative py-24 md:py-32 px-6"
      data-testid="features-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 
            className="font-heading text-2xl md:text-4xl font-bold tracking-tight uppercase border-l-4 border-neon-red pl-4"
            data-testid="features-title"
          >
            {sectionTitle}
          </h2>
          <p className="terminal-text text-sm mt-4 pl-5">
            <span className="prompt">$</span> {sectionSubtitle.replace('$ ', '')}
          </p>
        </motion.div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {displayFeatures.map((feature, index) => {
            const IconComponent = featureIcons[index] || Shield;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="cyber-card p-8 rounded-sm"
                data-testid={`feature-card-${index}`}
              >
                {/* Icon & Status */}
                <div className="flex items-start justify-between mb-6">
                  <div className="p-3 bg-surface-highlight rounded-sm border border-border-dim">
                    <IconComponent className="w-6 h-6 text-neon-red" />
                  </div>
                  <span className="terminal-text text-xs">
                    <span className="success">[{feature.status}]</span>
                  </span>
                </div>

                {/* Content */}
                <h3 className="font-heading text-lg font-bold tracking-tight mb-3 text-text-main">
                  {feature.title}
                </h3>
                <p className="terminal-text text-sm text-text-muted leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Access Section
const AccessSection = ({ settings }) => {
  const [hovering, setHovering] = useState(false);
  
  const casinoUrl = settings?.casino_url || "https://record.affiliates-paradise.com/_xYY56eOt5mUOMRDMpvVHzmNd7ZgqdRLk/1/";
  const casinoName = settings?.casino_name || "WEEZYBET";
  const terminalLines = settings?.terminal_lines || [
    "$ ./verify_access.sh",
    "[INFO] Scanning credentials...",
    "[INFO] Identity verified.",
    "[SUCCESS] Access granted to WEEZYBET.",
    "$ READY TO ENTER"
  ];
  const gateTitle = settings?.gate_title || "THE GATE IS";
  const gateHighlight = settings?.gate_highlight || "OPEN";
  const gateDescription = settings?.gate_description || "You've been selected. Few find this page. Fewer get in. Your credentials check out. Time to play.";
  const ctaPrefix = settings?.cta_prefix || "ENTER";
  const trustBadge = settings?.trust_badge || "256-BIT ENCRYPTED • ANONYMOUS • INSTANT";

  const renderTerminalLine = (line, index) => {
    if (line.includes("[SUCCESS]")) {
      const parts = line.split("[SUCCESS]");
      return (
        <p key={index} className="terminal-text text-sm mb-2">
          <span className="success">[SUCCESS]</span>{parts[1]}
        </p>
      );
    } else if (line.includes("[INFO]")) {
      const parts = line.split("[INFO]");
      return (
        <p key={index} className="terminal-text text-sm mb-2">
          <span className="text-text-muted">[INFO]</span>{parts[1]}
        </p>
      );
    } else if (line.startsWith("$") && index === terminalLines.length - 1) {
      return (
        <p key={index} className="terminal-text text-sm blink-cursor">
          <span className="prompt">$</span> <span className="text-neon-red">{line.substring(2)}</span>
        </p>
      );
    } else if (line.startsWith("$")) {
      return (
        <p key={index} className="terminal-text text-sm mb-2">
          <span className="prompt">$</span>{line.substring(1)}
        </p>
      );
    }
    return <p key={index} className="terminal-text text-sm mb-2">{line}</p>;
  };

  return (
    <section 
      id="access-section"
      className="relative py-24 md:py-40 px-6 overflow-hidden"
      data-testid="access-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-void via-surface to-void" />
      <div className="glow-red top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]" />
      
      {/* Intense Falling Chips for this section */}
      <FallingChips intense={true} />
      
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Terminal Window */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="glass rounded-sm overflow-hidden mb-12"
          data-testid="terminal-window"
        >
          {/* Terminal Header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-surface border-b border-border-dim">
            <div className="w-3 h-3 rounded-full bg-neon-red" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-neon-green" />
            <span className="ml-4 terminal-text text-xs">unmarketed@gateway ~ </span>
          </div>
          
          {/* Terminal Content with Weezy */}
          <div className="p-6 md:p-8 flex items-start gap-6">
            {/* Terminal Text */}
            <div className="flex-1 text-left">
              {terminalLines.map((line, index) => renderTerminalLine(line, index))}
            </div>
            
            {/* Animated Weezy Character */}
            <motion.div 
              className="hidden md:flex flex-shrink-0 relative items-center justify-center"
              style={{ width: 100, height: 100 }}
            >
              <motion.img
                src={WEEZY_URL}
                alt="Weezy"
                className="w-24 h-24 object-contain rounded-xl"
                style={{ 
                  imageRendering: "pixelated",
                }}
                animate={{ 
                  y: [0, -8, 0, -4, 0],
                  rotate: [-3, 3, -2, 2, -3],
                  scale: [1, 1.05, 1, 1.02, 1],
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                whileHover={{ 
                  scale: 1.15, 
                  rotate: [0, -10, 10, 0],
                  transition: { duration: 0.4 }
                }}
              />
              {/* Purple glow effect */}
              <motion.div 
                className="absolute inset-0 -z-10 rounded-xl"
                style={{
                  background: "radial-gradient(circle, rgba(139, 92, 246, 0.5) 0%, transparent 70%)",
                  filter: "blur(20px)"
                }}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.4, 0.7, 0.4]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-heading text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-tight mb-6"
          data-testid="access-headline"
        >
          {gateTitle} <span className="text-neon-red text-flicker">{gateHighlight}</span>
        </motion.h2>

        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="terminal-text text-sm md:text-base text-text-muted mb-10 max-w-lg mx-auto"
          data-testid="access-description"
        >
          {gateDescription}
        </motion.p>

        {/* Main CTA */}
        <motion.a
          href={casinoUrl}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          className="cyber-btn text-lg px-12 py-5"
          data-testid="access-cta"
        >
          <span className="relative z-10 flex items-center gap-3">
            <Eye className="w-5 h-5" />
            {ctaPrefix} {casinoName}
            <ChevronRight className={`w-5 h-5 transition-transform ${hovering ? 'translate-x-2' : ''}`} />
          </span>
        </motion.a>

        {/* Trust Badge */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="flex items-center justify-center gap-4 mt-10"
          data-testid="trust-badge"
        >
          <Lock className="w-4 h-4 text-neon-green" />
          <span className="terminal-text text-xs text-text-muted uppercase tracking-widest">
            {trustBadge}
          </span>
        </motion.div>
      </div>
    </section>
  );
};

// Footer with Language Selector
const Footer = ({ currentLang, onLanguageChange, settings }) => {
  const [showLangMenu, setShowLangMenu] = useState(false);
  const footerStatus = settings?.footer_status || "ALL SYSTEMS OPERATIONAL";
  const footerCopyright = settings?.footer_copyright || "INVITE ONLY";

  return (
    <footer 
      className="relative py-12 px-6 border-t border-border-dim"
      data-testid="footer"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo Small */}
          <div className="flex items-center gap-4">
            <Terminal className="w-5 h-5 text-neon-red" />
            <span className="font-heading text-sm tracking-wider">UNMARKETED.IO</span>
          </div>

          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-2 px-4 py-2 glass rounded-sm hover:border-neon-red transition-colors"
              data-testid="language-selector"
            >
              <Globe className="w-4 h-4 text-neon-red" />
              <span className="terminal-text text-xs">{LANGUAGES[currentLang]}</span>
            </button>
            
            {showLangMenu && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-surface border border-border-dim rounded-sm overflow-hidden min-w-[120px]"
              >
                {Object.entries(LANGUAGES).map(([code, name]) => (
                  <button
                    key={code}
                    onClick={() => {
                      onLanguageChange(code);
                      setShowLangMenu(false);
                    }}
                    className={`w-full px-4 py-2 terminal-text text-xs text-left hover:bg-surface-highlight transition-colors ${
                      currentLang === code ? 'text-neon-red' : 'text-text-muted'
                    }`}
                    data-testid={`lang-option-${code}`}
                  >
                    {name}
                  </button>
                ))}
              </motion.div>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-3">
            <div className="status-dot" />
            <span className="terminal-text text-xs text-text-muted">
              {footerStatus}
            </span>
          </div>

          {/* Contact & Copyright */}
          <div className="flex flex-col items-center md:items-end gap-2">
            <a 
              href="mailto:support@unmarketed.io" 
              className="terminal-text text-xs text-text-muted hover:text-neon-red transition-colors"
              data-testid="support-email"
            >
              support@unmarketed.io
            </a>
            <p className="terminal-text text-xs text-text-muted">
              © {new Date().getFullYear()} <span className="text-neon-red">UNMARKETED</span> • {footerCopyright}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Landing Page Component
const LandingPage = () => {
  const [settings, setSettings] = useState(null);
  const [currentLang, setCurrentLang] = useState("en");
  const [loading, setLoading] = useState(true);

  const loadSettings = async (lang) => {
    try {
      const response = await axios.get(`${API}/settings?lang=${lang}`);
      setSettings(response.data);
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  };

  const handleLanguageChange = async (lang) => {
    setCurrentLang(lang);
    localStorage.setItem("unmarketed_lang", lang);
    await loadSettings(lang);
  };

  useEffect(() => {
    const init = async () => {
      document.documentElement.style.scrollBehavior = 'smooth';
      
      // Detect language
      const detectedLang = await detectLanguage();
      setCurrentLang(detectedLang);
      
      // Load settings
      await loadSettings(detectedLang);
      setLoading(false);
    };
    
    init();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="scanlines" />
        <div className="noise" />
        <div className="status-dot w-4 h-4" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-void text-text-main overflow-hidden">
      {/* Falling Chips Background */}
      <FallingChips />
      
      {/* Overlays */}
      <div className="scanlines" />
      <div className="noise" />
      
      {/* Main Content */}
      <main>
        <HeroSection settings={settings} />
        <FeaturesSection settings={settings} />
        <AccessSection settings={settings} />
      </main>
      
      <Footer currentLang={currentLang} onLanguageChange={handleLanguageChange} settings={settings} />
    </div>
  );
};

// Main App with Routes
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
